import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { AiChat, AiChatEvent } from '@qtek/shared/models';
import { AiChatActions } from './ai-chat.actions';

export interface AiChatState {
  chats: AiChat[];
  eventsByChatId: Record<string, AiChatEvent[]>;
  isLoading: boolean;
  error: any | null;
  createdChatIdByMysid: Record<string, string | null>;
}

const initialState: AiChatState = {
  chats: [],
  eventsByChatId: {},
  isLoading: false,
  error: null,
  createdChatIdByMysid: {},
};

function upsertChat(list: AiChat[], chat: AiChat): AiChat[] {
  const i = list.findIndex(x => x.id === chat.id);
  if (i >= 0) {
    const next = list.slice();
    next[i] = { ...next[i], ...chat };
    return next;
  }
  return [...list, chat];
}

function toTimestamp(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return 0;
}

function sortChats(list: AiChat[]): AiChat[] {
  return [...(list ?? [])].sort((a, b) => {
    const at = toTimestamp(
      a.updatedAt ?? (a as any)?.createdAt ?? (a as any)?._uts
    );
    const bt = toTimestamp(
      b.updatedAt ?? (b as any)?.createdAt ?? (b as any)?._uts
    );
    return bt - at;
  });
}

function mergeChats(existing: AiChat[], incoming: AiChat[]): AiChat[] {
  if (!existing?.length) return sortChats(incoming ?? []);
  if (!incoming?.length) return sortChats(existing ?? []);

  const next = existing.slice();
  for (const chat of incoming) {
    if (!chat?.id) continue;
    const idx = next.findIndex(item => item.id === chat.id);
    if (idx >= 0) {
      next[idx] = { ...next[idx], ...chat };
    } else {
      next.push(chat);
    }
  }
  return sortChats(next);
}

/**
 * SD-2661: forget a chat completely — the list row, its cached transcript, and any record of it
 * as the chat a panel created for itself.
 *
 * That last part matters: a chat started fresh in a panel is created inside `qt-ai-chat` and
 * `createdChatIdByMysid` is the only place its id is kept, since the panel's own `selectedChatId`
 * stays null for it. Left behind, the component goes on resolving a deleted chat and the next
 * prompt is written against it — the BE answers `EntityNotFoundException: Chat not found`.
 */
function removeChat(state: AiChatState, id: string): AiChatState {
  if (!id) return state;

  const eventsByChatId = { ...state.eventsByChatId };
  delete eventsByChatId[id];

  const createdChatIdByMysid = Object.fromEntries(
    Object.entries(state.createdChatIdByMysid).filter(
      ([, chatId]) => chatId !== id
    )
  );

  return {
    ...state,
    chats: (state.chats ?? []).filter(chat => chat.id !== id),
    eventsByChatId,
    createdChatIdByMysid,
    isLoading: false,
  };
}

function eventKey(e: AiChatEvent): string {
  return `${e.chatId}|${e.ts}|${(e as any).src ?? ''}`;
}

function dedupeAppendEvents(
  existing: AiChatEvent[],
  incoming: AiChatEvent[]
): AiChatEvent[] {
  const byKey = new Map(existing.map(e => [eventKey(e), e]));
  for (const e of incoming) {
    const k = eventKey(e);
    byKey.set(k, { ...(byKey.get(k) || {}), ...e });
  }

  return Array.from(byKey.values()).sort((a, b) => a.ts - b.ts);
}

export const AiChatFeature = createFeature({
  name: 'aiChat',
  reducer: createReducer(
    initialState,

    on(
      AiChatActions.subscribeChats,
      AiChatActions.getChats,
      AiChatActions.createChat,
      AiChatActions.updateChat,
      state => ({ ...state, isLoading: true, error: null as any })
    ),

    on(AiChatActions.getChatsSuccess, (state, { chats }) => ({
      ...state,
      chats: mergeChats(state.chats, chats ?? []),
      isLoading: false,
    })),
    on(AiChatActions.getChatsFailure, (state, { error }) => ({
      ...state,
      isLoading: false,
      error,
    })),
    on(AiChatActions.createChatSuccess, (state, { chat, mysid }) => ({
      ...state,
      chats: upsertChat(state.chats, chat),
      isLoading: false,
      createdChatIdByMysid: mysid
        ? { ...state.createdChatIdByMysid, [mysid]: chat.id }
        : state.createdChatIdByMysid,
    })),
    on(AiChatActions.updateChatSuccess, (state, { chat }) => ({
      ...state,
      chats: upsertChat(state.chats, chat),
      isLoading: false,
    })),
    /**
     * SD-2661: drop the chat as soon as the user confirms, rather than waiting to be told.
     *
     * A write op gets no reply of its own — the BE soft-deletes and the change comes back as an
     * `upd` carrying `deletedAt`, broadcast over `transaction-events` to whoever still holds a
     * `Chat` subscription under a matching mysid. That is both slow (a Kafka round-trip) and
     * conditional: deleting the chat you are reading tears the panel's chat component down, and
     * its subscription with it, so the broadcast can come back to nobody. The row then sat in the
     * list until a reload — openable, but rejecting every prompt.
     *
     * Removing here needs no reply to arrive. A rejected delete still surfaces through the global
     * WS error dialog and the row returns on the next query.
     */
    // `id` is typed `string | number` by the generic WS request; a Chat id is always a UUID.
    on(AiChatActions.deleteChat, (state, { payload }) =>
      removeChat(state, String(payload?.id ?? ''))
    ),

    // The same removal driven by the broadcast — a delete in another tab or in the popped-out
    // window. Idempotent, so it is a no-op for the delete this session already applied above.
    on(AiChatActions.deleteChatSuccess, (state, { id }) =>
      removeChat(state, id)
    ),

    on(
      AiChatActions.createChatFailure,
      AiChatActions.updateChatFailure,
      AiChatActions.deleteChatFailure,
      (state, { error }) => ({ ...state, isLoading: false, error })
    ),

    on(AiChatActions.unsubscribeChats, (state, { mysid }) => {
      if (!mysid) {
        return { ...state, isLoading: false };
      }

      const nextCreated = { ...state.createdChatIdByMysid };
      delete nextCreated[mysid];

      return {
        ...state,
        isLoading: false,
        createdChatIdByMysid: nextCreated,
      };
    }),

    on(
      AiChatActions.subscribeChatEvents,
      AiChatActions.getChatEvents,
      AiChatActions.sendMessage,
      state => ({ ...state, isLoading: true, error: null as any })
    ),
    on(AiChatActions.getChatEventsSuccess, (state, { events }) => {
      const next = { ...state.eventsByChatId };
      for (const e of events ?? []) {
        if (!e?.chatId) continue;
        const curr = next[e.chatId] ?? [];
        next[e.chatId] = dedupeAppendEvents(curr, [e]);
      }
      return { ...state, eventsByChatId: next, isLoading: false };
    }),
    on(AiChatActions.getChatEventsFailure, (state, { error }) => ({
      ...state,
      isLoading: false,
      error,
    })),
    on(AiChatActions.unsubscribeChatEvents, state => ({
      ...state,
    }))
  ),
});

export const {
  name: aiChatFeatureKey,
  reducer: aiChatReducer,
  selectAiChatState,
  selectChats,
  selectError: selectAiChatError,
} = AiChatFeature;

// Selectors
export const selectChatIdByClientTag = (tag: string) =>
  createSelector(selectChats, chats => {
    const found = (chats ?? []).find(c => (c as any)?.ctx?.clientTag === tag);
    return found?.id ?? null;
  });

export const selectCreatedChatIdByMysid = (mysid: string) =>
  createSelector(selectAiChatState, s => s.createdChatIdByMysid[mysid] ?? null);

export const selectChatById = (id: string) =>
  createSelector(
    selectChats,
    chats => (chats ?? []).find(c => c.id === id) ?? null
  );

export const selectEventsByChatId = (chatId: string) =>
  createSelector(selectAiChatState, s => s.eventsByChatId[chatId] ?? []);

export const selectLastMessageByChatId = (chatId: string) =>
  createSelector(selectEventsByChatId(chatId), events =>
    events && events.length ? events[events.length - 1] : null
  );

const MAX_PREVIEW_LENGTH = 55;

function firstMessagePreview(events: AiChatEvent[] | undefined): string {
  if (!events?.length) return '';
  const first = events[0];
  const text = (first?.data ?? '').trim();
  if (!text) return '';
  if (text.length <= MAX_PREVIEW_LENGTH) return text;
  return text.slice(0, MAX_PREVIEW_LENGTH).trim() + '...';
}

/** Chats with preview set from API or derived from first message (no dates). */
export const selectChatsWithPreview = createSelector(
  selectAiChatState,
  (state): AiChat[] => {
    const chats = state.chats ?? [];
    const eventsByChatId = state.eventsByChatId ?? {};
    return chats.map(chat => ({
      ...chat,
      preview: chat.preview ?? firstMessagePreview(eventsByChatId[chat.id]),
    }));
  }
);

/**
 * SD-2661: the "Pinned" section — pinned chats, most recently pinned first.
 * Built on {@link selectChatsWithPreview} so the preview derivation is not duplicated.
 */
export const selectPinnedChats = createSelector(
  selectChatsWithPreview,
  (chats): AiChat[] =>
    chats
      .filter(chat => chat.pinnedAt != null)
      .sort((a, b) => toTimestamp(b.pinnedAt) - toTimestamp(a.pinnedAt))
);

/**
 * SD-2661: the "Recents" section — everything not pinned, keeping the existing
 * most-recently-updated-first order the flat list used.
 */
export const selectRecentChats = createSelector(
  selectChatsWithPreview,
  (chats): AiChat[] => sortChats(chats.filter(chat => chat.pinnedAt == null))
);
